import { useContext, useEffect, useMemo, useState } from 'react'

import {
  Box,
  DoughnutChart,
  Pagination,
  PartsProps,
  Table,
  TableHeader,
  Tabs,
  Text,
  getThemeColor,
} from '@fairfashion/design-system'
import { useTheme } from 'styled-components'

import { ICompanyRanking } from '../../../../sdk/company/company.types'
import { ProviderDashboardContext } from '../../provider-dashboard.context'

import * as Styles from './providers-clients-card.styles'
import { colors } from './providers-clients-card.utils'

const tabs = ['Fornecedores', 'Clientes']

export const ProvidersClientsCard = () => {
  const [tab, setTab] = useState(tabs[0])
  const [page, setPage] = useState(1)

  const theme = useTheme()

  const {
    firstClients,
    firstProviders,
    providers,
    clients,
    providersPages,
    clientsPages,
    actions,
  } = useContext(ProviderDashboardContext)

  useEffect(() => {
    actions.fetchInitialData()
  }, [])

  useEffect(() => {
    setPage(1)
  }, [tab])

  useEffect(() => {
    actions.fetchProvidersRanking(page)
    actions.fetchClientsRanking(page)
  }, [page, tab])

  const calculateParts = (companies: ICompanyRanking[]) => {
    const parts: PartsProps[] = companies.map((provider, index) => ({
      name: provider.name,
      value: parseFloat(provider.percentageItems),
      color: getThemeColor(colors[index], theme),
    }))

    const sum = parts.reduce((acc, part) => acc + part.value, 0)
    parts.push({
      name: 'Outros',
      value: parseFloat((100 - sum).toFixed(2)),
      color: getThemeColor(colors[5], theme),
    })

    return parts.filter((part) => part.value > 0)
  }

  const { firstData, data, pages, headers, parts } = useMemo(() => {
    if (tab === tabs[0]) {
      const providersTableHeaders: TableHeader[] = [
        {
          key: 'ranking',
          label: 'Ranking',
          width: '60px',
          customRender: (_, index) => (
            <Box justifyContent="center" width="100%">
              <Text>{index + 1 + (page - 1) * 5}</Text>
            </Box>
          ),
        },
        { key: 'name', label: 'Fornecedor', minWidth: '250px' },
        {
          key: 'percent',
          label: '% comprada',
          width: '80px',
          align: 'center',
          customRender: (row) => (
            <Box justifyContent="center" width="100%">
              <Text>{row.percentageItems}%</Text>
            </Box>
          ),
        },
      ]

      return {
        firstData: firstProviders,
        data: providers,
        pages: providersPages,
        headers: providersTableHeaders,
        parts: calculateParts(firstProviders),
      }
    }

    const clientsTableHeaders: TableHeader[] = [
      {
        key: 'ranking',
        label: 'Ranking',
        width: '80px',
        customRender: (_, index) => (
          <Box justifyContent="center" width="100%">
            <Text>{index + 1 + (page - 1) * 5}</Text>
          </Box>
        ),
      },
      { key: 'name', label: 'Cliente', minWidth: '250px' },
      {
        key: 'percent',
        label: '% vendida',
        width: '80px',
        align: 'center',
        customRender: (row) => (
          <Box justifyContent="center" width="100%">
            <Text>{row.percentageItems}%</Text>
          </Box>
        ),
      },
    ]

    return {
      firstData: firstClients,
      data: clients,
      pages: clientsPages,
      headers: clientsTableHeaders,
      parts: calculateParts(firstClients),
    }
  }, [tab, page, firstProviders, firstClients, providers, clients])

  return (
    <Styles.Card>
      <Tabs tabs={tabs} onChangeTab={setTab} active={tab} />

      <Box width="100%" gap={3} direction="column">
        <Box width="100%" gap={3} alignItems="center" flexWrap="wrap">
          <DoughnutChart
            parts={parts}
            totalValue={100}
            size={140}
            showTotalValue={false}
            showTooltip
            tooltipValueFormatter={(value) => `${value}%`}
          />
          <Styles.Labels flex={1} flexWrap="wrap" alignItems="center" gap={2}>
            {firstData.map((company, index) => (
              <Box alignItems="center" gap={2} key={`company-${index}`}>
                <Styles.ColoredBox color={colors[index]} />
                <Styles.LabelText size="small">{company.name}</Styles.LabelText>
              </Box>
            ))}
            <Box alignItems="center" gap={2}>
              <Styles.ColoredBox color={colors[5]} />
              <Styles.LabelText size="small">Outros</Styles.LabelText>
            </Box>
          </Styles.Labels>
        </Box>
        <Table
          data={data}
          headers={headers}
          spacing="small"
          footer={
            <Box padding={1} width="100%" justifyContent="center">
              <Pagination
                total={pages}
                current={page}
                onChange={(_page) => setPage(_page)}
              />
            </Box>
          }
        />
      </Box>
    </Styles.Card>
  )
}
